import React, { useState, useEffect } from 'react';
import { login, logout, isAuthenticated, getPosts, addPost, updatePost, deletePost } from '../services/blogService';
import { formatDateTime } from '../utils/formatters';
import RichTextEditor from '../components/RichTextEditor';
import { generateArticle } from '../services/aiService';
import { useSettings } from '../context/SettingsContext';
import { updateSiteSettings } from '../services/settingsService';

const Admin = () => {
  const [isAuth, setIsAuth] = useState(isAuthenticated());
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  
  // Dashboard State
  const [posts, setPosts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [activeTab, setActiveTab] = useState('posts'); // 'posts' or 'appearance'
  
  // Global Settings State
  const { settings, refetchSettings } = useSettings();
  const [globalTitle, setGlobalTitle] = useState('');
  const [globalDescription, setGlobalDescription] = useState('');
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  
  // Post Fields
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('أخبار السوق');
  const [coverImage, setCoverImage] = useState('');
  const [author, setAuthor] = useState('أسعار الذهب');
  
  // SEO Fields
  const [focusKeyword, setFocusKeyword] = useState('');
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [slug, setSlug] = useState('');

  // AI Writer State
  const [geminiApiKey, setGeminiApiKey] = useState(() => localStorage.getItem('geminiApiKey') || '');
  const [aiDescription, setAiDescription] = useState('');
  const [aiKeywords, setAiKeywords] = useState('');
  const [aiFont, setAiFont] = useState('auto');
  const [aiColor, setAiColor] = useState('auto');
  const [aiImages, setAiImages] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);

  const CATEGORIES = ['أخبار السوق', 'تحليلات', 'نصائح للمبتدئين', 'اقتصاد'];
  
  const AI_FONTS = [
    { label: 'تلقائي (اختيار الذكاء الاصطناعي)', value: 'auto' },
    { label: 'Cairo', value: 'Cairo' },
    { label: 'Tajawal', value: 'Tajawal' },
    { label: 'Arial', value: 'Arial' }
  ];

  const AI_COLORS = [
    { label: 'تلقائي (اختيار الذكاء الاصطناعي)', value: 'auto' },
    { label: 'ذهبي كلاسيكي', value: '#D4AF37' },
    { label: 'أزرق داكن (احترافي)', value: '#1E3A8A' },
    { label: 'أخضر (اقتصادي)', value: '#16A34A' },
    { label: 'أسود فحمي', value: '#1F2937' }
  ];

  // Save API key to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('geminiApiKey', geminiApiKey);
  }, [geminiApiKey]);

  const fetchPosts = async () => {
    const fetchedPosts = await getPosts();
    setPosts(fetchedPosts);
  };

  useEffect(() => {
    if (isAuth) {
      fetchPosts();
    }
  }, [isAuth]);

  useEffect(() => {
    if (settings) {
      setGlobalTitle(settings.siteTitle || '');
      setGlobalDescription(settings.siteDescription || '');
    }
  }, [settings]);

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setIsSavingSettings(true);
    const success = await updateSiteSettings({
      siteTitle: globalTitle,
      siteDescription: globalDescription,
    });
    if (success) {
      refetchSettings();
      alert('تم حفظ إعدادات المظهر بنجاح!');
    } else {
      alert('حدث خطأ أثناء حفظ الإعدادات.');
    }
    setIsSavingSettings(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    const token = await login(username, password);
    if (token) {
      setIsAuth(true);
    } else {
      setError('بيانات الدخول غير صحيحة');
    }
  };

  const handleLogout = () => {
    logout();
    setIsAuth(false);
  };

  const resetForm = () => {
    setEditingPost(null);
    setTitle('');
    setContent('');
    setCategory('أخبار السوق');
    setCoverImage('');
    setFocusKeyword('');
    setSeoTitle('');
    setSeoDescription('');
    setSlug('');
    setAuthor('أسعار الذهب');
    setAiDescription('');
    setAiKeywords('');
    setAiFont('auto');
    setAiColor('auto');
    setAiImages('');
  };

  const handleSubmitPost = async (e) => {
    e.preventDefault();
    const postData = { title, content, category, coverImage, author, focusKeyword, seoTitle, seoDescription, slug };
    
    if (editingPost) {
      await updatePost(editingPost.id, postData);
    } else {
      await addPost(postData);
    }
    resetForm();
    await fetchPosts();
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setTitle(post.title);
    setContent(post.content || '');
    setCategory(post.category || 'أخبار السوق');
    setCoverImage(post.coverImage || '');
    setFocusKeyword(post.focusKeyword || '');
    setSeoTitle(post.seoTitle || '');
    setSeoDescription(post.seoDescription || '');
    setSlug(post.slug || '');
    setAuthor(post.author || 'أسعار الذهب');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm('هل أنت متأكد من حذف هذا المقال؟')) {
      await deletePost(id);
      await fetchPosts();
    }
  };

  const getCounterColor = (length, min, max) => {
    if (length === 0) return 'var(--color-text-tertiary)';
    if (length >= min && length <= max) return 'var(--color-positive)';
    return 'var(--color-negative)';
  };

  // Yoast-like SEO Analyzer
  const analyzeSEO = () => {
    const checks = [];
    const plainTextContent = content.replace(/<[^>]*>?/gm, '');
    const wordCount = plainTextContent.split(/\s+/).filter(w => w.length > 0).length;
    
    // Check 1: Content Length
    if (wordCount >= 300) {
      checks.push({ status: 'good', text: `طول النص ممتاز (${wordCount} كلمة).` });
    } else if (wordCount >= 150) {
      checks.push({ status: 'ok', text: `طول النص مقبول (${wordCount} كلمة)، يفضل زيادة المحتوى لأكثر من 300 كلمة.` });
    } else {
      checks.push({ status: 'bad', text: `النص قصير جداً (${wordCount} كلمة). أضف المزيد من المحتوى لتصدر نتائج البحث.` });
    }

    // Check 2: Headings and formatting
    if (content.includes('<h2') || content.includes('<h3')) {
      checks.push({ status: 'good', text: 'استخدام جيد للعناوين الفرعية (H2, H3) لتقسيم المقال.' });
    } else {
      checks.push({ status: 'bad', text: 'المقال لا يحتوي على عناوين فرعية (H2, H3). قسم المقال بعناوين لتسهيل القراءة.' });
    }

    // Check 3: Focus Keyword Analysis
    if (!focusKeyword) {
      checks.push({ status: 'bad', text: 'لم تقم بتحديد الكلمة المفتاحية المستهدفة (Focus Keyword).' });
    } else {
      const keyword = focusKeyword.trim().toLowerCase();
      
      // Keyword in SEO Title
      const t = (seoTitle || title).toLowerCase();
      if (t.includes(keyword)) {
        checks.push({ status: 'good', text: 'الكلمة المفتاحية موجودة في عنوان الـ SEO.' });
      } else {
        checks.push({ status: 'bad', text: 'الكلمة المفتاحية غير موجودة في عنوان الـ SEO.' });
      }

      // Keyword in SEO Description
      const d = (seoDescription).toLowerCase();
      if (d.includes(keyword)) {
        checks.push({ status: 'good', text: 'الكلمة المفتاحية موجودة في وصف الـ Meta Description.' });
      } else {
        checks.push({ status: 'ok', text: 'يفضل إضافة الكلمة المفتاحية لوصف المقال (Meta Description).' });
      }

      // Keyword in first paragraph
      const firstPar = plainTextContent.substring(0, 200).toLowerCase();
      if (firstPar.includes(keyword)) {
        checks.push({ status: 'good', text: 'الكلمة المفتاحية تظهر في بداية المقال (أول فقرة).' });
      } else {
        checks.push({ status: 'ok', text: 'تأكد من كتابة الكلمة المفتاحية في أول 100 كلمة من المقال.' });
      }

      // Keyword Density
      const keywordRegex = new RegExp(keyword, 'g');
      const matches = plainTextContent.toLowerCase().match(keywordRegex);
      const keywordCount = matches ? matches.length : 0;
      const density = wordCount > 0 ? ((keywordCount * keyword.split(' ').length) / wordCount) * 100 : 0;

      if (keywordCount === 0) {
        checks.push({ status: 'bad', text: 'الكلمة المفتاحية لا تظهر إطلاقاً في محتوى المقال!' });
      } else if (density > 2.5) {
        checks.push({ status: 'bad', text: `تكرار الكلمة المفتاحية عالي جداً (${keywordCount} مرات). قد يعتبره جوجل (حشو كلمات سبام).` });
      } else if (density >= 0.5) {
        checks.push({ status: 'good', text: `كثافة الكلمة المفتاحية ممتازة (${keywordCount} مرات).` });
      } else {
        checks.push({ status: 'ok', text: `حاول تكرار الكلمة المفتاحية أكثر في المحتوى (تظهر ${keywordCount} مرة فقط).` });
      }
    }

    // Check 4: SEO Title Length
    const tLen = seoTitle.length > 0 ? seoTitle.length : title.length;
    if (tLen >= 50 && tLen <= 60) {
      checks.push({ status: 'good', text: 'طول عنوان הـ SEO مثالي جداً.' });
    } else if (tLen > 60) {
      checks.push({ status: 'bad', text: 'عنوان الـ SEO طويل جداً، سيتم قصه في نتائج بحث جوجل.' });
    } else if (tLen > 0) {
      checks.push({ status: 'ok', text: 'عنوان الـ SEO قصير. حاول إطالته ليكون بين 50 و 60 حرفاً.' });
    }

    return checks;
  };

  const calculateSeoScore = (checks) => {
    if (checks.length === 0) return 0;
    const goodChecks = checks.filter(c => c.status === 'good').length;
    const okChecks = checks.filter(c => c.status === 'ok').length;
    
    // Basic weighting: good = 1, ok = 0.5, bad = 0
    const rawScore = goodChecks + (okChecks * 0.5);
    return Math.round((rawScore / checks.length) * 100);
  };

  const seoChecks = analyzeSEO();
  const seoScore = calculateSeoScore(seoChecks);

  const getSeoScoreData = (score) => {
    if (score >= 80) return { label: 'ممتاز 🤩', color: 'var(--color-positive)' };
    if (score >= 50) return { label: 'جيد 🙂', color: 'var(--color-gold)' };
    if (score > 0) return { label: 'يحتاج تحسين 😕', color: 'var(--color-negative)' };
    return { label: 'أضف بيانات', color: 'var(--color-text-tertiary)' };
  };

  const seoData = getSeoScoreData(seoScore);

  // Generate Snippet Data
  const getSnippetData = () => {
    const finalTitle = seoTitle || title || 'عنوان المقال التجريبي';
    const finalSlug = slug || title.trim().toLowerCase().replace(/\s+/g, '-') || 'رابط-المقال';
    
    let finalDesc = seoDescription;
    if (!finalDesc) {
      const plain = content.replace(/<[^>]*>?/gm, '');
      finalDesc = plain ? plain.substring(0, 150) + '...' : 'وصف المقال سيظهر هنا. قم بكتابة وصف مخصص أو سيتم استخراج جزء من النص تلقائياً...';
    }

    return { title: finalTitle, url: `https://goldprice.com/post/${finalSlug}`, description: finalDesc };
  };

  const snippet = getSnippetData();

  if (!isAuth) {
    return (
      <div className="admin-login-page fade-in">
        <div className="admin-login-card">
          <h2>تسجيل دخول المسؤول</h2>
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>اسم المستخدم</label>
              <input 
                type="text" 
                value={username} 
                onChange={(e) => setUsername(e.target.value)} 
                placeholder="admin"
                required 
              />
            </div>
            <div className="form-group">
              <label>كلمة المرور</label>
              <input 
                type="password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                placeholder="admin123"
                required 
              />
            </div>
            {error && <p className="error-text">{error}</p>}
            <button type="submit" className="login-btn">دخول</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard fade-in">
      <header className="admin-header">
        <div className="container admin-header-content">
          <h1>لوحة تحكم المدونة (CMS)</h1>
          <button onClick={handleLogout} className="logout-btn">تسجيل خروج</button>
        </div>
      </header>

      <div className="container">
        <div className="admin-tabs">
          <button 
            className={`admin-tab-btn ${activeTab === 'posts' ? 'active' : ''}`}
            onClick={() => setActiveTab('posts')}
          >
            إدارة المقالات
          </button>
          <button 
            className={`admin-tab-btn ${activeTab === 'appearance' ? 'active' : ''}`}
            onClick={() => setActiveTab('appearance')}
          >
            المظهر و SEO
          </button>
        </div>

        {activeTab === 'posts' && (
          <>
            <section className="post-form-section">
          <div className="card">
            <h3>{editingPost ? 'تعديل مقال' : 'إضافة مقال جديد'}</h3>
            <form onSubmit={handleSubmitPost}>
              <div className="form-group">
                <label>عنوان المقال</label>
                <input 
                  type="text" 
                  value={title} 
                  onChange={(e) => setTitle(e.target.value)} 
                  required 
                />
              </div>

              <div className="form-group">
                <label>اسم الكاتب</label>
                <input 
                  type="text" 
                  value={author} 
                  onChange={(e) => setAuthor(e.target.value)} 
                  placeholder="مثال: أحمد حسن"
                />
              </div>

              <div className="form-row">
                <div className="form-group half-width">
                  <label>القسم</label>
                  <select 
                    value={category} 
                    onChange={(e) => setCategory(e.target.value)}
                    className="custom-select"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group half-width">
                  <label>رابط الصورة البارزة (URL)</label>
                  <input 
                    type="url" 
                    value={coverImage} 
                    onChange={(e) => setCoverImage(e.target.value)} 
                    placeholder="https://example.com/image.jpg"
                  />
                  <small className="help-text">يفضل إدخال رابط صورة لتجنب امتلاء الذاكرة المؤقتة.</small>
                </div>
              </div>

              {/* AI Writer Section */}
              <div className="ai-writer-section">
                <div className="ai-writer-header">
                  <h4>✨ كاتب الذكاء الاصطناعي الذكي (AI Writer)</h4>
                  <span className="ai-badge">Gemini Pro</span>
                </div>
                <p className="ai-writer-desc">أدخل تفاصيل المقال، وسيقوم الذكاء الاصطناعي بكتابته بالكامل وتنسيقه بالألوان والخطوط مع دمج الصور بذكاء.</p>
                
                <div className="form-group" style={{ background: 'rgba(255,255,255,0.5)', padding: '12px', borderRadius: '8px', border: '1px solid var(--color-border)' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    🔑 مفتاح Gemini API
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input 
                      type={showApiKey ? "text" : "password"} 
                      value={geminiApiKey} 
                      onChange={(e) => setGeminiApiKey(e.target.value)} 
                      placeholder="AIzaSy..."
                      dir="ltr"
                      className="ltr-input"
                      style={{ background: 'white', paddingRight: '40px' }}
                    />
                    <button 
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--color-text-tertiary)',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title={showApiKey ? "إخفاء" : "إظهار"}
                    >
                      {showApiKey ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      )}
                    </button>
                  </div>
                  <small className="help-text">يتم حفظ المفتاح محلياً في متصفحك (Local Storage) ولن يتم رفعه للسيرفر.</small>
                </div>

                <div className="form-row">
                  <div className="form-group half-width">
                    <label>وصف موجز للمقال المطلوب</label>
                    <textarea 
                      rows="2" 
                      value={aiDescription} 
                      onChange={(e) => setAiDescription(e.target.value)} 
                      placeholder="مثال: مقال تحليلي عن حركة الذهب وتوقعات الأسبوع..."
                    ></textarea>
                  </div>
                  <div className="form-group half-width">
                    <label>الكلمات المفتاحية المستهدفة</label>
                    <textarea 
                      rows="2" 
                      value={aiKeywords} 
                      onChange={(e) => setAiKeywords(e.target.value)} 
                      placeholder="مثال: أسعار الذهب, شراء الذهب"
                    ></textarea>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group half-width">
                    <label>نوع الخط المفضل (Font)</label>
                    <select value={aiFont} onChange={(e) => setAiFont(e.target.value)} className="custom-select">
                      {AI_FONTS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group half-width">
                    <label>لون العناوين والنصوص البارزة (Color)</label>
                    <select value={aiColor} onChange={(e) => setAiColor(e.target.value)} className="custom-select">
                      {AI_COLORS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>روابط صور لإدراجها في المقال (رابط في كل سطر)</label>
                  <textarea 
                    rows="3" 
                    value={aiImages} 
                    onChange={(e) => setAiImages(e.target.value)} 
                    placeholder="https://example.com/gold1.jpg&#10;https://example.com/gold2.jpg"
                    dir="ltr"
                    className="ltr-input"
                  ></textarea>
                  <small className="help-text">سيقوم الذكاء الاصطناعي بتوزيع هذه الصور بذكاء بين فقرات المقال.</small>
                </div>

                {aiError && <p className="error-text" style={{marginBottom: '12px'}}>{aiError}</p>}
                
                <button 
                  type="button" 
                  className={`ai-generate-btn ${aiLoading ? 'loading' : ''}`}
                  disabled={aiLoading || !title}
                  onClick={async () => {
                    if (!geminiApiKey) {
                      setAiError('الرجاء إدخال مفتاح Gemini API أولاً.');
                      return;
                    }
                    if (!title) {
                      setAiError('الرجاء كتابة "عنوان المقال" بالأعلى أولاً.');
                      return;
                    }
                    setAiLoading(true);
                    setAiError('');
                    try {
                      const result = await generateArticle(geminiApiKey, title, aiDescription, aiKeywords, aiFont, aiColor, aiImages);
                      setContent(result.content);
                      if (result.seoTitle) setSeoTitle(result.seoTitle);
                      if (result.seoDescription) setSeoDescription(result.seoDescription);
                      if (result.slug) setSlug(result.slug);
                      if (aiKeywords) setFocusKeyword(aiKeywords.split(',')[0].trim());
                    } catch (err) {
                      setAiError(err.message || 'حدث خطأ أثناء توليد المقال.');
                    } finally {
                      setAiLoading(false);
                    }
                  }}
                >
                  {aiLoading ? (
                    <><span className="ai-spinner"></span> جاري توليد وتنسيق المقال بذكاء...</>
                  ) : (
                    '✨ ابدأ الكتابة بالذكاء الاصطناعي'
                  )}
                </button>
              </div>

              <div className="form-group editor-group">
                <label>محتوى المقال</label>
                <RichTextEditor value={content} onChange={setContent} />
              </div>

              {/* SEO Section */}
              <div className="seo-section">
                <div className="seo-header-flex">
                  <h4>إعدادات محركات البحث (SEO) الشاملة</h4>
                  <div className="seo-score-badge" style={{ backgroundColor: seoData.color }}>
                    <span className="score-number">{seoScore}/100</span>
                    <span className="score-label">{seoData.label}</span>
                  </div>
                </div>

                <div className="form-group focus-keyword-group">
                  <label>الكلمة المفتاحية المستهدفة (Focus Keyword) 🗝️</label>
                  <input 
                    type="text" 
                    value={focusKeyword} 
                    onChange={(e) => setFocusKeyword(e.target.value)} 
                    placeholder="مثال: أسعار الذهب في السعودية"
                    className="focus-keyword-input"
                  />
                  <small className="help-text">اختر الكلمة التي تريد أن يتصدر بها المقال في بحث جوجل ليتم تحليله بناءً عليها.</small>
                </div>
                
                <div className="yoast-grid">
                  <div className="yoast-inputs">
                    <div className="form-group">
                      <label>الرابط المخصص (Slug)</label>
                      <input 
                        type="text" 
                        value={slug} 
                        onChange={(e) => setSlug(e.target.value)} 
                        placeholder="مثال: توقعات-اسعار-الذهب-اليوم"
                        dir="ltr"
                        className="ltr-input"
                      />
                      <small className="help-text">اتركه فارغاً ليتم توليده تلقائياً من عنوان المقال.</small>
                    </div>

                    <div className="form-group">
                      <div className="label-with-counter">
                        <label>عنوان الـ SEO (يظهر في جوجل)</label>
                        <span className="counter" style={{ color: getCounterColor(seoTitle.length, 50, 60) }}>
                          {seoTitle.length}/60
                        </span>
                      </div>
                      <input 
                        type="text" 
                        value={seoTitle} 
                        onChange={(e) => setSeoTitle(e.target.value)} 
                        placeholder="اتركه فارغاً لاستخدام العنوان الأساسي..."
                      />
                    </div>

                    <div className="form-group">
                      <div className="label-with-counter">
                        <label>وصف الـ SEO (Meta Description)</label>
                        <span className="counter" style={{ color: getCounterColor(seoDescription.length, 120, 160) }}>
                          {seoDescription.length}/160
                        </span>
                      </div>
                      <textarea 
                        rows="3" 
                        value={seoDescription} 
                        onChange={(e) => setSeoDescription(e.target.value)} 
                        placeholder="اكتب وصفاً جذاباً يظهر أسفل الرابط في جوجل (الأفضل بين 120 و 160 حرف)..."
                      ></textarea>
                    </div>
                  </div>

                  <div className="yoast-analysis">
                    {/* Google Snippet Preview */}
                    <div className="google-snippet-preview">
                      <div className="snippet-header">معاينة بحث جوجل الموبايل</div>
                      <div className="snippet-content">
                        <div className="snippet-url">{snippet.url}</div>
                        <div className="snippet-title">{snippet.title}</div>
                        <div className="snippet-desc">{snippet.description}</div>
                      </div>
                    </div>

                    {/* SEO Checklist */}
                    <div className="seo-checklist">
                      <h5>تحليل الـ SEO للكلمة المفتاحية</h5>
                      {seoChecks.length === 0 ? (
                        <p className="empty-text">اكتب بعض المحتوى لرؤية التحليل.</p>
                      ) : (
                        <ul className="checklist-items">
                          {seoChecks.map((check, idx) => (
                            <li key={idx} className={`check-item status-${check.status}`}>
                              <span className="status-dot"></span>
                              <span className="check-text">{check.text}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-btn">
                  {editingPost ? 'تحديث المقال' : 'نشر المقال'}
                </button>
                {editingPost && (
                  <button type="button" onClick={resetForm} className="cancel-btn">
                    إلغاء
                  </button>
                )}
              </div>
            </form>
          </div>
        </section>

        <section className="posts-list-section">
          <h3>المقالات المنشورة ({posts.length})</h3>
          <div className="admin-posts-grid">
            {posts.map((post) => (
              <div key={post.id} className="admin-post-item">
                <div className="admin-post-info">
                  <h4>{post.title}</h4>
                  <span className="post-meta">
                    <span className="badge">{post.category || 'عام'}</span> 
                    {formatDateTime(post.date)}
                  </span>
                </div>
                <div className="admin-post-actions">
                  <button onClick={() => handleEdit(post)} className="edit-btn">تعديل</button>
                  <button onClick={() => handleDelete(post.id)} className="delete-btn">حذف</button>
                </div>
              </div>
            ))}
            {posts.length === 0 && <p className="empty-text">لا توجد مقالات منشورة بعد.</p>}
          </div>
        </section>
          </>
        )}

        {activeTab === 'appearance' && (
          <section className="appearance-section fade-in">
            <div className="card">
              <h3>إعدادات المظهر و SEO العام</h3>
              <p className="help-text" style={{marginBottom: '20px'}}>هذه الإعدادات ستُطبق على الصفحة الرئيسية وتؤثر على ظهور موقعك ككل في محركات البحث.</p>
              
              <form onSubmit={handleSaveSettings}>
                <div className="form-group">
                  <div className="label-with-counter">
                    <label>عنوان الموقع (Site Title)</label>
                    <span className="counter" style={{ color: getCounterColor(globalTitle.length, 10, 60) }}>
                      {globalTitle.length}/60
                    </span>
                  </div>
                  <input 
                    type="text" 
                    value={globalTitle} 
                    onChange={(e) => setGlobalTitle(e.target.value)} 
                    placeholder="مثال: أسعار الذهب اليوم | تحديث لحظي"
                    required
                  />
                  <small className="help-text">يظهر في علامة التبويب وفي نتائج بحث جوجل كعنوان رئيسي.</small>
                </div>

                <div className="form-group">
                  <div className="label-with-counter">
                    <label>وصف الموقع (Meta Description)</label>
                    <span className="counter" style={{ color: getCounterColor(globalDescription.length, 120, 160) }}>
                      {globalDescription.length}/160
                    </span>
                  </div>
                  <textarea 
                    rows="3" 
                    value={globalDescription} 
                    onChange={(e) => setGlobalDescription(e.target.value)} 
                    placeholder="اكتب وصفاً جذاباً يشرح ما يقدمه الموقع..."
                    required
                  ></textarea>
                  <small className="help-text">يظهر أسفل العنوان في نتائج جوجل، مهم جداً لجذب الزوار.</small>
                </div>

                <div className="yoast-analysis" style={{marginTop: '30px', marginBottom: '30px'}}>
                  <div className="google-snippet-preview">
                    <div className="snippet-header">معاينة بحث جوجل (الصفحة الرئيسية)</div>
                    <div className="snippet-content">
                      <div className="snippet-url">https://yoursite.com</div>
                      <div className="snippet-title">{globalTitle || 'عنوان الموقع'}</div>
                      <div className="snippet-desc">{globalDescription || 'وصف الموقع سيظهر هنا...'}</div>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="submit-btn" disabled={isSavingSettings}>
                    {isSavingSettings ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
                  </button>
                </div>
              </form>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default Admin;
